/**
 * A BALANÇA DE DOIS PRATOS — a equação como objetos sobre dois pratos.
 *
 * Sacos iguais, todos com a mesma quantidade dentro, e pesos de um quilo. Enquanto os
 * dois pratos valem o mesmo, o braço fica reto; tirar peça de um prato só faz a balança
 * pender — e é essa inclinação que ensina, sozinha, por que se faz a mesma coisa dos
 * dois lados.
 *
 * A BALANÇA INCLINA, ao contrário da prancha da gangorra (`render/gangorra.ts`). Lá,
 * inclinar entregaria a resposta: bastaria procurar a posição em que ela fica reta.
 * Aqui, o aluno já sabe que os pratos valem o mesmo — é a hipótese do problema —, e
 * inclinar é a consequência do que ele acabou de fazer, não uma pista sobre o valor do
 * saco. Quem tenta chegar ao resultado tirando peça até a balança ficar reta é pego na
 * correção, que lê a trajetória e não só o estado final.
 *
 * A INCLINAÇÃO É LIMITADA e não proporcional à diferença: ela diz "os pratos deixaram
 * de valer o mesmo, e para que lado", e não quanto. Um ângulo proporcional seria uma
 * régua da diferença, e aí sim o desenho responderia a pergunta.
 *
 * As peças ficam SEMPRE EM PÉ, mesmo com o braço torto — é o que acontece numa balança
 * de verdade, onde os pratos são pendurados.
 */

import { conta, type NomeId } from '../core/pt.ts';
import { txt } from '../schema/richtext.ts';
import { scene, type Scene, type Shape } from './scene.ts';
import type { Vec2 } from '../geom/vec.ts';

/** A unidade do desenho. Todo o resto é múltiplo dela. */
const U = 22;
const SACO_L = 1.15 * U;
const SACO_A = 1.45 * U;
const VAO_SACO = 0.12 * U;
const LADO_PESO = 0.62 * U;
const PASSO_PESO = 0.72 * U;
/**
 * Peças por fileira. Os pesos vão de seis em seis e os sacos de quatro em quatro: é o
 * que faz a pilha mais alta possível (4 sacos, 12 pesos) caber entre o prato e o braço
 * sem encostar nele.
 */
const COLS_PESO = 6;
const COLS_SACO = 4;
/** Meia envergadura do braço. */
const L = 4.4 * U;
/** Do braço até a superfície do prato. */
const HASTE = 3.4 * U;
const PRATO_L = 5.2 * U;
/** O braço nunca passa disto: a inclinação diz o lado, não o tamanho da diferença. */
const INCLINACAO_MAX = 9;

export interface PratoDesenho {
  readonly sacos: number;
  readonly pesos: number;
}

export interface OpcoesBalanca {
  readonly esq: PratoDesenho;
  readonly dir: PratoDesenho;
  /** O que está escrito em cada saco: quase sempre "x". */
  readonly incognita: string;
  /**
   * Qual prato pesa mais, para a inclinação. Vem de fora porque só o item sabe o valor
   * do saco — e o desenho não pode descobri-lo sozinho.
   */
  readonly pende: 'esq' | 'dir' | 'nenhum';
  /** Peças que acabaram de sair, desenhadas apagadas no lugar onde estavam. */
  readonly fantasmas?: { readonly prato: 'esq' | 'dir'; readonly indice: number }[];
  readonly title: string;
  readonly desc: string;
}

const rad = (g: number): number => (g * Math.PI) / 180;

export function balanca(o: OpcoesBalanca): Scene {
  const ang = o.pende === 'nenhum' ? 0 : o.pende === 'esq' ? INCLINACAO_MAX : -INCLINACAO_MAX;
  const th = rad(ang);
  const ponta = (lado: -1 | 1): Vec2 => ({
    x: lado * L * Math.cos(th),
    y: lado * L * Math.sin(th),
  });

  const shapes: Shape[] = [];

  // A coluna e o pé, que não se mexem.
  shapes.push({ t: 'seg', a: { x: 0, y: -HASTE }, b: { x: 0, y: 0 }, role: 'given' });
  shapes.push({
    t: 'poly',
    pts: [
      { x: -1.5 * U, y: -HASTE },
      { x: 1.5 * U, y: -HASTE },
      { x: 0.55 * U, y: -0.5 * U },
      { x: -0.55 * U, y: -0.5 * U },
    ],
    closed: true,
    role: 'muted',
    fill: 'muted-fill',
  });

  // O braço, e a charneira no meio.
  const pe = ponta(-1);
  const pd = ponta(1);
  shapes.push({ t: 'seg', a: pe, b: pd, role: 'given' });
  shapes.push({ t: 'circle', c: { x: 0, y: 0 }, r: 0.26 * U, role: 'given', fill: 'surface' });

  for (const [lado, prato] of [
    [-1, o.esq],
    [1, o.dir],
  ] as const) {
    const p = ponta(lado as -1 | 1);
    const base = { x: p.x, y: p.y - HASTE };
    // A haste é vertical: o prato pendura, não gira com o braço.
    shapes.push({ t: 'seg', a: p, b: base, role: 'auxiliary' });
    shapes.push({
      t: 'seg',
      a: { x: base.x - PRATO_L / 2, y: base.y },
      b: { x: base.x + PRATO_L / 2, y: base.y },
      role: 'given',
    });
    // As bordas do prato, curtas, para ele ler como prato e não como risco.
    for (const s of [-1, 1]) {
      shapes.push({
        t: 'seg',
        a: { x: base.x + (s * PRATO_L) / 2, y: base.y },
        b: { x: base.x + (s * PRATO_L) / 2 + s * 0.3 * U, y: base.y + 0.5 * U },
        role: 'given',
      });
    }

    // As peças ficam CENTRADAS no prato e empilham para cima: é como um punhado de
    // coisas se acomoda, e é o que mantém a figura legível quando o prato esvazia.
    // A ordem — sacos e depois pesos, esquerda e depois direita — é contrato com
    // `ui/balanca.ts`, que usa estes retângulos como alvos de clique.
    const linhaDe = (i: number, cols: number): number => Math.floor(i / cols);
    const naLinha = (i: number, total: number, cols: number): number =>
      Math.min(cols, total - linhaDe(i, cols) * cols);

    for (let i = 0; i < prato.sacos; i++) {
      const larg = naLinha(i, prato.sacos, COLS_SACO) * (SACO_L + VAO_SACO) - VAO_SACO;
      const x = base.x - larg / 2 + (i % COLS_SACO) * (SACO_L + VAO_SACO);
      const y = base.y + linhaDe(i, COLS_SACO) * (SACO_A + VAO_SACO);
      shapes.push({
        t: 'rect',
        at: { x, y, w: SACO_L, h: SACO_A },
        role: 'given',
        fill: 'surface',
      });
      shapes.push({
        t: 'label',
        at: { x: x + SACO_L / 2, y: y + SACO_A / 2 },
        text: [txt(o.incognita)],
        anchor: 'c',
      });
    }

    // Os pesos empilham acima dos sacos, em fileiras de seis: contam-se de relance.
    const yPesos =
      base.y + Math.ceil(prato.sacos / COLS_SACO) * (SACO_A + VAO_SACO) + (prato.sacos > 0 ? 0 : 0);
    for (let i = 0; i < prato.pesos; i++) {
      const larg = naLinha(i, prato.pesos, COLS_PESO) * PASSO_PESO - (PASSO_PESO - LADO_PESO);
      const x = base.x - larg / 2 + (i % COLS_PESO) * PASSO_PESO;
      const y = yPesos + linhaDe(i, COLS_PESO) * PASSO_PESO;
      shapes.push({
        t: 'rect',
        at: { x, y, w: LADO_PESO, h: LADO_PESO },
        role: 'given',
        fill: 'ink',
      });
    }
  }

  // Enquadramento FIXO, calculado para a pilha mais alta que o item admite — e não
  // para as peças de agora. Com o enquadramento seguindo o conteúdo, a balança mudava
  // de tamanho a cada peça tirada, e a figura piscava a cada clique.
  const meia = L + PRATO_L / 2 + 0.5 * U;
  const topo = 0.9 * U;
  const fundo = -(HASTE + 0.9 * U);
  return scene(shapes, {
    viewBox: { x: -meia, y: fundo, w: 2 * meia, h: topo - fundo },
    a11y: { title: o.title, desc: o.desc },
  });
}

/** Descricao textual da balanca -- o `alt` obrigatorio, gerado do estado. */
export function describeBalanca(
  esq: PratoDesenho,
  dir: PratoDesenho,
  recipiente: NomeId,
  incognita: string,
  unidade: string,
  pende: 'esq' | 'dir' | 'nenhum',
): string {
  const lado = (p: PratoDesenho): string => {
    const partes: string[] = [];
    if (p.sacos > 0) partes.push(`${conta(p.sacos, recipiente).adj('marcado')} com ${incognita}`);
    if (p.pesos > 0) partes.push(`${conta(p.pesos, 'peso')} de 1 ${unidade}`);
    return partes.length > 0 ? partes.join(' e ') : 'nada';
  };
  const fim =
    pende === 'nenhum'
      ? 'O braço está reto: os dois pratos valem o mesmo.'
      : `O braço está pendendo para a ${pende === 'esq' ? 'esquerda' : 'direita'}: os pratos deixaram de valer o mesmo.`;
  return `Balança de dois pratos. No prato da esquerda, ${lado(esq)}. No da direita, ${lado(dir)}. ${fim}`;
}
